import type { Asset, Evaluation, Idea, Product, ResearchEntry, WorkflowEvent } from './types'

const now = Date.now()
const ago = (hours: number) => new Date(now - hours * 3_600_000).toISOString()

export const demoIdeas: Idea[] = [
  {
    id: 'demo-1', idea_number: 18, owner_id: 'demo', origin: 'customer',
    title: 'Sunday Reset Planner', raw_idea: 'A guided weekly reset system for high-output parents.',
    status: 'awaiting_ceo', primary_niche: 'Intentional parents', secondary_niche: 'Weekly planning', micro_niche: null,
    buyer_summary: 'Busy parents who want their week to feel designed, not survived.', emotional_driver: 'Control without rigidity',
    recommended_format: 'Undated planner + digital prompts', opportunity_score: 91, evidence_grade: 'a', ceo_priority: 1,
    created_at: ago(72), updated_at: ago(1),
  },
  {
    id: 'demo-2', idea_number: 17, owner_id: 'demo', origin: 'research',
    title: 'The Story Keeper', raw_idea: 'Interview cards and archive book for preserving family stories.',
    status: 'council_review', primary_niche: 'Family legacy', secondary_niche: 'Gifts', micro_niche: null,
    buyer_summary: 'Adult children who fear losing the stories only their parents know.', emotional_driver: 'Preservation and belonging',
    recommended_format: 'Card deck + archival book', opportunity_score: 84, evidence_grade: 'b', ceo_priority: 2,
    created_at: ago(168), updated_at: ago(6),
  },
  {
    id: 'demo-3', idea_number: 16, owner_id: 'demo', origin: 'ceo',
    title: 'First Year Field Notes', raw_idea: 'A practical, honest memory system for new parents.',
    status: 'design', primary_niche: 'New parents', secondary_niche: 'Memory keeping', micro_niche: null,
    buyer_summary: 'New parents who want to remember the year without maintaining a scrapbook.', emotional_driver: 'Hold onto what moves too fast',
    recommended_format: 'Prompted journal', opportunity_score: 88, evidence_grade: 'a', ceo_priority: 1,
    created_at: ago(300), updated_at: ago(24),
  },
  {
    id: 'demo-4', idea_number: 15, owner_id: 'demo', origin: 'team',
    title: 'Campfire Questions', raw_idea: 'A screen-free conversation game for family trips.',
    status: 'market_test', primary_niche: 'Family travel', secondary_niche: 'Conversation games', micro_niche: null,
    buyer_summary: 'Parents trying to create connection during travel downtime.', emotional_driver: 'Togetherness',
    recommended_format: 'Pocket card deck', opportunity_score: 78, evidence_grade: 'b', ceo_priority: 3,
    created_at: ago(500), updated_at: ago(48),
  },
  {
    id: 'demo-5', idea_number: 14, owner_id: 'demo', origin: 'customer',
    title: 'Household Rhythm Cards', raw_idea: 'Visual household routines that children can own.',
    status: 'validated', primary_niche: 'Family systems', secondary_niche: 'Routines', micro_niche: null,
    buyer_summary: 'Parents tired of repeating the same instructions every day.', emotional_driver: 'Shared responsibility',
    recommended_format: 'Magnetic cards', opportunity_score: 86, evidence_grade: 'a', ceo_priority: 2,
    created_at: ago(700), updated_at: ago(80),
  },
]

export const demoEvents: WorkflowEvent[] = [
  { id: 'e1', idea_id: 'demo-1', owner_id: 'demo', event_type: 'status_changed', from_status: 'council_review', to_status: 'awaiting_ceo', note: 'Council synthesis is ready for your call.', created_at: ago(1) },
  { id: 'e2', idea_id: 'demo-2', owner_id: 'demo', event_type: 'evaluation_added', from_status: null, to_status: null, note: 'Market evaluator scored the opportunity at 84.', created_at: ago(6) },
  { id: 'e3', idea_id: 'demo-3', owner_id: 'demo', event_type: 'asset_added', from_status: null, to_status: null, note: 'Creative direction v2 is ready to review.', created_at: ago(24) },
  { id: 'e4', idea_id: 'demo-4', owner_id: 'demo', event_type: 'status_changed', from_status: 'ready_for_test', to_status: 'market_test', note: 'Landing page test is live.', created_at: ago(48) },
]

export const demoProducts: Product[] = [
  { id: 'p1', idea_id: 'demo-3', name: 'First Year Field Notes', status: 'design', selling_price: 34, unit_cost: 7.2, launch_date: '2026-11-12' },
  { id: 'p2', idea_id: 'demo-4', name: 'Campfire Questions', status: 'market_test', selling_price: 24, unit_cost: 4.5, launch_date: null },
  { id: 'p3', idea_id: 'demo-5', name: 'Household Rhythm Cards', status: 'validated', selling_price: 38, unit_cost: 8.4, launch_date: '2027-01-20' },
]

export const demoResearch: ResearchEntry[] = [
  { id: 'r1', idea_id: 'demo-1', research_type: 'buyer_signal', title: 'Sunday night planning language', summary: 'Parents describe the weekly reset as relief from carrying the whole household in their head.', source_url: null, source_name: 'Interview synthesis', confidence: 'a', created_at: ago(2) },
  { id: 'r2', idea_id: 'demo-2', research_type: 'demand_signal', title: 'Legacy gifts show durable demand', summary: 'Family-story prompts repeatedly surface as gifts around milestone birthdays and holidays.', source_url: null, source_name: 'Demand scan', confidence: 'b', created_at: ago(9) },
]

export const demoEvaluations: Evaluation[] = [
  { id: 'v1', idea_id: 'demo-1', evaluator_role: 'Market skeptic', overall_score: 91, evidence_grade: 'a', recommendation: 'Approve a narrow physical prototype.', critique: 'Keep the promise about emotional relief, not productivity theater.', created_at: ago(2) },
  { id: 'v2', idea_id: 'demo-2', evaluator_role: 'Buyer advocate', overall_score: 84, evidence_grade: 'b', recommendation: 'Validate gifting language before design.', critique: 'The archive matters more than the card game.', created_at: ago(7) },
]

export const demoAssets: Asset[] = [
  { id: 'a1', idea_id: 'demo-3', product_id: 'p1', asset_type: 'creative_direction', label: 'Creative direction v2', external_url: null, version: 2, approval_status: 'review', created_at: ago(24) },
  { id: 'a2', idea_id: 'demo-4', product_id: 'p2', asset_type: 'landing_page', label: 'Market-test landing page', external_url: null, version: 1, approval_status: 'approved', created_at: ago(48) },
]
